import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Modal,
  Fade,
  Backdrop,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Stack,
  Tabs,
  Tab,
  TextField,
  Button,
  Box
} from '@material-ui/core';
import PropTypes from 'prop-types';
import UserTable from '../UserTable';
import RoleTable from '../RoleTable';
import { useIsFeatureFlagEnabled } from '../../../utils/FeatureFlags';
import useVrackApi from '../../../hooks/useVrackApi';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  p: 4
};

TeamDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  closeHandler: PropTypes.func,
  team: PropTypes.string
};

export default function TeamDetailsModal({ isOpen, closeHandler, team }) {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [company, setCompany] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [originalCompany, setOriginalCompany] = useState('');
  const [originalProjectCode, setOriginalProjectCode] = useState('');

  const isVrackAdminEnabled = useIsFeatureFlagEnabled('vrack_admin');
  const currentUserEmail = useSelector((state) => state.userDetails?.value?.email) ?? '';
  // Initialize useVrackApi hooks for different endpoints
  const fetchUsersApi = useVrackApi('GET', `/teams/${team}/users`);
  const fetchRolesApi = useVrackApi('GET', `/admin/teams/${team}/roles`);
  const fetchMetadataApi = useVrackApi('GET', `/identities/${team}/metadata`);
  const saveMetadataApi = useVrackApi('POST', `/admin/teams/${team}/metadata`);

  // Reset form when modal opens with a different team
  useEffect(() => {
    if (!isOpen) {
      setCompany('');
      setProjectCode('');
      setOriginalCompany('');
      setOriginalProjectCode('');
    }
  }, [isOpen]);

  useEffect(() => {
    // initial call to get team details on modal open
    if (isOpen) {
      fetchTeamsRoles();
      fetchTeamsUsers();
      fetchTeamMetadata();
    }
    // eslint-disable-next-line
  }, [team, isOpen]);

  const fetchTeamsUsers = async () => {
    try {
      const data = await fetchUsersApi.fetchData();
      setUsers(data);
      return data;
    } catch (error) {
      console.error('failed to get list of teams ', error.message);
    }
    return [];
  };

  const fetchTeamsRoles = async () => {
    try {
      const data = await fetchRolesApi.fetchData();
      setRoles(data);
      return data;
    } catch (error) {
      console.error('failed to get list of teams ', error.message);
    }
    return null;
  };

  const fetchTeamMetadata = async () => {
    try {
      const data = await fetchMetadataApi.fetchData();
      const { company, project_code: projectCode } = data;
      setCompany(company || '');
      setProjectCode(projectCode || '');
      setOriginalCompany(company || '');
      setOriginalProjectCode(projectCode || '');
      return data;
    } catch (error) {
      console.error('failed to get team metadata', error.message);
      // Initialize with empty strings if metadata not available
      setCompany('');
      setProjectCode('');
      setOriginalCompany('');
      setOriginalProjectCode('');
    }
    return null;
  };

  const saveTeamMetadata = async () => {
    try {
      await saveMetadataApi.fetchData({
        data: {
          company,
          project_code: projectCode
        }
      });
      // Update original values after successful save
      setOriginalCompany(company);
      setOriginalProjectCode(projectCode);
      // Show success snackbar
      dispatch(
        setOpenSnackbar({
          message: 'Team metadata saved successfully',
          severity: 'success',
          duration: 5000
        })
      );
    } catch (error) {
      console.error('failed to save team metadata', error.message);
      // Show error snackbar
      dispatch(
        setOpenSnackbar({
          message: `Failed to save team metadata: ${error.message}`,
          severity: 'error',
          duration: 5000
        })
      );
    }
  };

  const isCurrentUserTeamAdmin = () => {
    if (!users || !currentUserEmail) return false;
    const currentUser = users.find((user) => user.email === currentUserEmail);
    return currentUser?.admin || false;
  };
  const hasChanges = company !== originalCompany || projectCode !== originalProjectCode;

  return (
    <Modal
      open={isOpen}
      sx={{ outline: 'none' }}
      onClose={closeHandler}
      aria-labelledby="team-details-modal"
      aria-describedby="modal to display team details"
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Fade in={isOpen}>
        <Grid direction="row" sx={style} container>
          <Grid item xs={12} md={1} lg={2} onClick={closeHandler} />
          <Grid item xs={12} md={10} lg={8}>
            <Card>
              <CardHeader sx={{ padding: 2 }} title={`${team} Details`} />
              <Divider />
              <CardContent sx={{ maxHeight: '85vh', overflow: 'auto' }}>
                <Stack spacing={2}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1">Team Details</Typography>
                      <Grid container spacing={2} marginBottom={2} marginTop={0}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            variant="outlined"
                            size="small"
                            helperText="Company associated to the team"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Project / Cost Code"
                            value={projectCode}
                            onChange={(e) => setProjectCode(e.target.value)}
                            variant="outlined"
                            size="small"
                            helperText="Project / Cost Code associated to the team"
                          />
                        </Grid>
                      </Grid>
                      <Box display="flex" justifyContent="flex-end">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveTeamMetadata}
                          disabled={!hasChanges}
                        >
                          Save Changes
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  <Card>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                      <Tab label="Users" />
                      {isVrackAdminEnabled && <Tab label="Roles" />}
                    </Tabs>
                    <CardContent sx={{ p: 3 }}>
                      {tabValue === 0 && (
                        <UserTable
                          team={team}
                          updateUsers={fetchTeamsUsers}
                          users={users}
                          variant="team"
                          size="small"
                          showHeader
                          currentUserIsTeamAdmin={isCurrentUserTeamAdmin()}
                        />
                      )}
                      {tabValue === 1 && (
                        <RoleTable
                          team={team}
                          updateRoles={fetchTeamsRoles}
                          roles={roles}
                          showHeader
                        />
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} med={1} lg={2} onClick={closeHandler} />
        </Grid>
      </Fade>
    </Modal>
  );
}
